import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { founderService } from '../lib/founder-service';
import { founderAnalyticsService } from '../lib/founder-analytics-service';
import { BarChart3, Users, DollarSign, Activity, TrendingUp, AlertCircle, LogOut, MessageSquare } from 'lucide-react';
import { OverviewTab } from './founder/OverviewTab';
import { UsersTab } from './founder/UsersTab';
import { RevenueTab } from './founder/RevenueTab';
import { EngagementTab } from './founder/EngagementTab';
import { PerformanceTab } from './founder/PerformanceTab';

type TabType = 'overview' | 'users' | 'revenue' | 'engagement' | 'performance';

export function FounderDashboard() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  useEffect(() => {
    checkAccess();
  }, [user]);

  useEffect(() => {
    if (hasAccess) {
      loadDashboardData();
    }
  }, [hasAccess]);

  const checkAccess = async () => {
    if (!user) {
      navigate('/');
      return;
    }

    const isFounder = await founderService.isFounder(user.id);

    if (!isFounder) {
      navigate('/dashboard');
      return;
    }

    setHasAccess(true);
    setIsLoading(false);
  };

  const loadDashboardData = async () => {
    try {
      const data = await founderAnalyticsService.getAllDashboardData();
      setDashboardData(data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const handleRefresh = async () => {
    await loadDashboardData();
  };

  const handleLogout = () => {
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#8CAFA8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6B7280]">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: BarChart3 },
    { id: 'users' as TabType, label: 'Users', icon: Users },
    { id: 'revenue' as TabType, label: 'Revenue', icon: DollarSign },
    { id: 'engagement' as TabType, label: 'Engagement', icon: Activity },
    { id: 'performance' as TabType, label: 'Performance', icon: TrendingUp }
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-[#8CAFA8] w-10 h-10 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-[#1F2937]">Mentra Founder Dashboard</h1>
                <p className="text-sm text-[#6B7280]">Welcome back, {profile?.full_name || 'Founder'}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 px-3 py-2 text-[#6B7280] hover:text-[#1F2937] transition"
              >
                <TrendingUp className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden md:inline text-sm">Refresh</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-[#6B7280] hover:text-[#1F2937] transition"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden md:inline text-sm">Exit</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {dashboardData?.alerts && dashboardData.alerts.length > 0 && (
          <div className="mb-6 bg-orange-50 border-l-4 border-orange-400 p-4 rounded-[16px]">
            <div className="flex items-center gap-3">
              <AlertCircle className="text-orange-600" size={20} />
              <div>
                <h3 className="text-sm font-medium text-orange-800">Active Alerts</h3>
                <p className="text-sm text-orange-700 mt-1">
                  You have {dashboardData.alerts.length} active alert{dashboardData.alerts.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-[16px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-gray-100 mb-6">
          <div className="border-b border-gray-100">
            <nav className="flex -mb-px">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors
                      ${activeTab === tab.id
                        ? 'border-[#8CAFA8] text-[#8CAFA8]'
                        : 'border-transparent text-[#6B7280] hover:text-[#1F2937] hover:border-gray-200'
                      }
                    `}
                  >
                    <Icon size={18} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && <OverviewTab data={dashboardData} onRefresh={loadDashboardData} />}
            {activeTab === 'users' && <UsersTab data={dashboardData} />}
            {activeTab === 'revenue' && <RevenueTab data={dashboardData} />}
            {activeTab === 'engagement' && <EngagementTab data={dashboardData} />}
            {activeTab === 'performance' && <PerformanceTab data={dashboardData} />}
          </div>
        </div>
      </div>
    </div>
  );
}
